import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import find from 'lodash.find';
import scrollToComponent from 'react-scroll-to-component';

import CircularProgress from '@material-ui/core/CircularProgress';

import FormattedMessageWrappedInSpan from './FormattedMessageWrappedInSpan';

import GenerateVideoMutation from '../../mutations/GenerateVideoMutation';
import GenerateZipMutation from '../../mutations/GenerateZipMutation';

import { ORIGINAL, SOCIAL_MEDIA } from '../../utils/variables';
import environment from '../../environment';

class DownloadDropdown extends Component {
  constructor(props) {
    super(props);

    // map props to useful state object
    const values = props.values.map(value => ({
      id: value,
      isPending: false,
      url: null,
      failed: false,
    }));

    this.state = {
      expanded: false,
      values: values,
    };
    this.handleExpandMenu = this.handleExpandMenu.bind(this);
    this.handleButtonClicked = this.handleButtonClicked.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside(event) {
    if (this.dropdownRef && !this.dropdownRef.contains(event.target)) {
      if (this.state.expanded) {
        this.setState({ expanded: false });
      }
    }
  }

  handleExpandMenu(newValue) {
    this.setState({ expanded: newValue }, () => {
      if (newValue) {
        if (window.innerWidth < 768) {
          scrollToComponent(this.dropdownRef, { align: 'top' });
        }
      }
    });
  }

  handleButtonClicked(value) {
    if (value.url) {
      this._download(value.url);
    } else {
      this._generateDownloadLink(value.id);
    }
  }

  _generateDownloadLink(id) {
    this._updateState(id, 'isPending', true);
    switch (this.props.downloadType) {
      case 'zip':
        this._generateZipDownloadLink(id);
        return;
      case 'video':
        this._generateVideoDownloadLink(id);
        return;
      default:
        return;
    }
  }

  _generateZipDownloadLink(value) {
    GenerateZipMutation.commit(
      environment,
      this.props.eventSgId,
      value,
      (errorMsg, url) => {
        if (!errorMsg) {
          this._updateState(value, 'url', url);
          this._download(url);
        } else {
          this._updateState(value, 'failed', true);
        }
        this._updateState(value, 'isPending', false);
      }
    );
  }

  _generateVideoDownloadLink(startNumber) {
    GenerateVideoMutation.commit(
      environment,
      this.props.eventSgId,
      startNumber,
      (error, url) => {
        if (!error) {
          this._updateState(startNumber, 'url', url);
          this._download(url);
        } else {
          this._updateState(startNumber, 'failed', true);
        }
        this._updateState(startNumber, 'isPending', false);
      }
    );
  }

  _download(url) {
    window.location.href = url;
  }

  _updateState(id, key, newValue) {
    let stateCopy = [...this.state.values];
    const obj = find(stateCopy, v => v.id === id);
    obj[key] = newValue;
    if (key === 'url') {
      // If the URL is set, the request was successful
      obj.failed = false;
    }
    this.setState({
      values: stateCopy,
    });
  }

  _renderDropdownMainLabel() {
    switch (this.props.downloadType) {
      case 'zip':
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.mainLabel.zip"
            defaultMessage="Download photos as ZIP"
          />
        );
      case 'video':
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.mainLabel.video"
            defaultMessage="Download video"
          />
        );
      case 'certificate':
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.mainLabel.certificate"
            defaultMessage="Download certificate"
          />
        );
      default:
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.mainLabel.default"
            defaultMessage="Download"
          />
        );
    }
  }

  _renderDropdownItemLabel(value) {
    const { downloadType } = this.props;
    if (downloadType === 'zip') {
      if (value === ORIGINAL) {
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.requestLabel.zip.original"
            defaultMessage="High resolution"
          />
        );
      } else if (value === SOCIAL_MEDIA) {
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.requestLabel.zip.socialmedia"
            defaultMessage="Social media resolution"
          />
        );
      } else {
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.requestLabel.zip.startNumber"
            defaultMessage="Start number {startNumber}"
            values={{ startNumber: value }}
          />
        );
      }
    } else if (downloadType === 'video') {
      return (
        <FormattedMessageWrappedInSpan
          id="downloadDropdown.requestLabel.video"
          defaultMessage="Start number {startNumber}"
          values={{ startNumber: value }}
        />
      );
    }
  }

  _renderDropdownItemFailedLabel(value) {
    const { downloadType } = this.props;
    if (downloadType === 'zip') {
      if (value === ORIGINAL) {
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.failedLabel.zip.original"
            defaultMessage="Retry (high resolution)"
          />
        );
      } else if (value === SOCIAL_MEDIA) {
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.failedLabel.zip.socialmedia"
            defaultMessage="Retry (social media resolution)"
          />
        );
      } else {
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.failedLabel.zip.startNumber"
            defaultMessage="Retry (start number {startNumber})"
            values={{ startNumber: value }}
          />
        );
      }
    } else if (downloadType === 'video') {
      return (
        <FormattedMessageWrappedInSpan
          id="downloadDropdown.failedLabel.video"
          defaultMessage="Retry (start number {startNumber})"
          values={{ startNumber: value }}
        />
      );
    }
  }

  render() {
    const { expanded, values } = this.state;
    const { fullWidth, downloadType } = this.props;
    return (
      <div
        ref={div => (this.dropdownRef = div)}
        className={classNames('download-dropdown-container', {
          'full-width': fullWidth,
        })}
      >
        <div
          className={classNames('download-dropdown-main', {
            expanded: expanded,
            'zip-download': downloadType === 'zip',
          })}
          onClick={() => this.handleExpandMenu(!expanded)}
        >
          {this._renderDropdownMainLabel()}
          <div className="download-dropdown-expand-icon-container">
            <span
              className={classNames('download-dropdown-expand-icon', {
                expanded: expanded,
              })}
            />
          </div>
        </div>
        <div className={classNames('download-dropdown-content', { expanded: expanded })}>
          {values.map((value, index) => {
            return (
              <button
                key={index}
                className={classNames('download-dropdown-item', {
                  disabled: value.isPending,
                })}
                disabled={value.isPending}
                onClick={() => this.handleButtonClicked(value)}
              >
                {!value.failed && this._renderDropdownItemLabel(value.id)}
                {value.failed && this._renderDropdownItemFailedLabel(value.id)}
                {value.isPending && (
                  <div className="download-dropdown-expand-icon-container">
                    <CircularProgress
                      classes={{ circle: 'circular-progress-circle-copy-color-medium' }}
                      size={20}
                      thickness={3}
                    />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
}

DownloadDropdown.defaultProps = {
  downloadType: 'none',
};

DownloadDropdown.propTypes = {
  eventSgId: PropTypes.number,
  fullWidth: PropTypes.bool,
  downloadType: PropTypes.oneOf(['zip', 'video', 'certificate']).isRequired,
  values: PropTypes.array,
};

export default DownloadDropdown;
